"use client";

import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "@shared/src/components/ui/select";
import { SelectTrigger as CustomTrigger } from "@shared/src/components/ui/select-custom-trigger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from "@shared/src/components/ui/dialog";
import { cn } from "@shared/src/utils/cn";
// import { Check } from "lucide-react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { X } from "lucide-react";
import { CustomCheckbox } from "@shared/src/components/ui/common/form-components/CustomCheckbox";
import { SearchInput } from "@shared/src/components/ui/common/inputs/SearchInput";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Spinner } from "@shared/src/components/ui/common/Spinner";
import { useMediaQuery } from "@shared/src/hooks/useMediaQuery";

interface Option {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string | React.ReactNode;
  isLocked?: boolean;
}

interface CustomSelectProps {
  label: string;
  error?: boolean;
  className?: string;
  containerClassName?: string;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  placeholder?: string;
  hideLabel?: boolean;
  options: Option[];
  isMulti?: boolean;
  showSearch?: boolean;
  variant?: "dropdown" | "modal";
  layout?: "grid" | "list";
  isSpecialSelect?: boolean;
  isLoading?: boolean;
  icon?: React.ReactNode;
  showIconOnlySm?: boolean;
  hideTrigger?: boolean;
  disabled?: boolean;
  initialOpen?: boolean;
  modalTitle?: string;
  modal?: boolean;
  modalDescription?: string;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
  required?: boolean;
  hint?: string;
  preventClose?: boolean;
  size?: "sm" | "md";
}

export function CustomSelect({
  className,
  label,
  error,
  containerClassName,
  value,
  onValueChange,
  onOpenChange,
  placeholder,
  options,
  hideLabel = false,
  isMulti = false,
  showSearch = true,
  variant = "dropdown",
  layout = "grid",
  isSpecialSelect = false,
  icon,
  showIconOnlySm = false,
  disabled = false,
  isLoading = false,
  hideTrigger = false,
  initialOpen = false,
  modal = true,
  modalTitle,
  modalDescription,
  children,
  required = false,
  preventClose = false,
  hint,
  size = "md",
}: CustomSelectProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isModalOpen, setIsModalOpen] = React.useState(initialOpen);
  const [tempSelection, setTempSelection] = React.useState<string>(typeof value === "string" ? value : "");
  const [isOpen, setIsDropdownOpen] = React.useState(initialOpen);
  const isMobile = useMediaQuery("(max-width: 768px)");
  if (isMobile) {
    variant = "modal";
    modal = true;
  }

  function handleDropdownOpen(open: boolean) {
    // console.log("networkmo handleOpen", open);
    setIsDropdownOpen(open);
    // onOpenChange?.(open);
  }
  function handleModalOpen(open: boolean) {
    console.log("networkmo handleOpen", open);
    setIsModalOpen(open);
    onOpenChange?.(open);
  }

  // Update tempSelection when value changes
  React.useEffect(() => {
    if (!isMulti) {
      setTempSelection(typeof value === "string" ? value : "");
    }
  }, [value, isMulti]);

  const selectedValues = isMulti ? (Array.isArray(value) ? value : []) : [];

  const handleSelect = (optionValue: string) => {
    if (isMulti) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onValueChange?.(newValues);
    } else {
      setTempSelection(optionValue);
      onValueChange?.(optionValue);
      if (variant === "modal") {
        handleModalOpen(false);
      }
    }
  };

  const handleModalSelect = () => {
    if (isMulti) {
      onValueChange?.(selectedValues);
    } else if (tempSelection) {
      onValueChange?.(tempSelection);
    }
    handleModalOpen(false);
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    return options.filter(
      option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.value.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  const handleRemoveValue = (valueToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the dropdown
    if (isMulti) {
      const newValues = selectedValues.filter(v => v !== valueToRemove);
      onValueChange?.(newValues);
    }
  };

  // Handler for click events when disabled
  const handleDisabledClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "relative w-full rounded-2xl border border-border-tertiary bg-surfaces-bg-primary transition-all duration-200",
          error ? "border-border-error" : isFocused && "border-border-focus",
          isMulti ? (size === "sm" ? "min-h-12" : "min-h-[62px]") : size === "sm" ? "h-12" : "h-[62px]",
          disabled && "opacity-50 cursor-not-allowed",
          containerClassName,
        )}
        onClick={handleDisabledClick}
      >
        {!hideLabel && (
          <label
            className={cn(
              "absolute -top-2 left-3 !text-small bg-surfaces-bg-primary px-2 transition-all duration-200",
              error ? "text-text-error" : isFocused ? "text-text-brand" : "text-text-tertiary",
            )}
          >
            {label}
            {required && <span className="text-text-error ml-1">*</span>}
          </label>
        )}

        {variant === "modal" ? (
          <div
            onClick={e => {
              if (!disabled) {
                handleModalOpen(true);
              }
              handleDisabledClick(e);
            }}
            className={cn("cursor-pointer h-full", disabled && "cursor-not-allowed")}
          >
            {/* Icon-only view for small screens */}
            {showIconOnlySm && icon && (
              <div className="sm:hidden p-4 flex-shrink-0 w-full h-full flex items-center justify-center cursor-pointer">
                {icon}
              </div>
            )}
            {/* Full select view */}
            <div className={cn("h-full", showIconOnlySm && "max-sm:hidden")}>
              <div
                className={cn(
                  "h-full px-4 flex items-center justify-between",
                  value ? "text-text-primary" : "text-text-tertiary",
                )}
              >
                <div className="flex-1 min-w-0">
                  {isMulti ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {icon && <span className="flex-shrink-0 text-text-primary">{icon}</span>}
                      {selectedValues.length === 0 ? (
                        <span className="text-text-tertiary truncate overflow-hidden whitespace-nowrap max-w-full">
                          {placeholder}
                        </span>
                      ) : (
                        selectedValues.map(val => {
                          const option = options.find(opt => opt.value === val);
                          if (!option) return null;
                          return (
                            <div
                              key={option.value}
                              className="flex items-center gap-2 bg-surfaces-bg-secondary rounded-lg px-3 py-1.5 group"
                            >
                              <span className="text-text-primary text-small-2 sm:text-medium truncate overflow-hidden whitespace-nowrap max-w-full">
                                {option.label}
                              </span>
                              <button
                                onClick={e => handleRemoveValue(option.value, e)}
                                className="ml-1 p-0.5 rounded-full hover:bg-surfaces-bg-tertiary transition-colors"
                              >
                                <X className="h-3 w-3 text-text-tertiary hover:text-text-primary" />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 min-w-0">
                      {value ? (
                        <div
                          className={cn(
                            "flex items-center gap-2 min-w-0",
                            isSpecialSelect && "bg-surfaces-bg-secondary rounded-lg px-3 py-1.5",
                          )}
                        >
                          {icon && <span className="flex-shrink-0 text-text-primary">{icon}</span>}
                          {options.find(opt => opt.value === value)?.icon && (
                            <span className="flex-shrink-0 text-text-primary">
                              {options.find(opt => opt.value === value)?.icon}
                            </span>
                          )}
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-text-primary max-sm:text-small flex gap-2 truncate overflow-hidden whitespace-nowrap max-w-full">
                              {options.find(opt => opt.value === value)?.label}
                              {options.find(opt => opt.value === value)?.description &&
                                (typeof options.find(opt => opt.value === value)?.description === "string" ? (
                                  <span className="text-text-success text-small max-sm:text-small">
                                    {options.find(opt => opt.value === value)?.description}
                                  </span>
                                ) : (
                                  options.find(opt => opt.value === value)?.description
                                ))}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-text-tertiary truncate overflow-hidden whitespace-nowrap max-w-full">
                          {placeholder}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <MdKeyboardArrowDown className="h-5 w-5 text-text-tertiary" />
              </div>
            </div>
          </div>
        ) : isMulti ? (
          <PopoverPrimitive.Root
            open={disabled ? false : isOpen}
            onOpenChange={open => !disabled && handleDropdownOpen(open)}
          >
            <PopoverPrimitive.Trigger asChild>
              <div
                className={cn(
                  cn(
                    "h-full px-4 flex items-center justify-between cursor-pointer",
                    size === "sm" ? "min-h-12" : "min-h-[62px]",
                  ),
                  value ? "text-text-primary" : "text-text-tertiary",
                  className,
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap py-2">
                    {icon && <span className="flex-shrink-0 text-text-primary">{icon}</span>}
                    {selectedValues.length === 0 ? (
                      <span className="text-text-tertiary truncate overflow-hidden whitespace-nowrap max-w-full">
                        {placeholder}
                      </span>
                    ) : (
                      selectedValues.map(val => {
                        const option = options.find(opt => opt.value === val);
                        if (!option) return null;
                        return (
                          <div
                            key={option.value}
                            className="flex items-center gap-2 bg-surfaces-bg-secondary rounded-lg px-3 py-1.5 group"
                          >
                            {option.icon && <span className="flex-shrink-0 text-text-primary">{option.icon}</span>}
                            <span className="text-text-primary text-small-2 sm:text-medium truncate overflow-hidden whitespace-nowrap max-w-full">
                              {option.label}
                            </span>
                            <button
                              onClick={e => handleRemoveValue(option.value, e)}
                              className="ml-1 p-0.5 rounded-full hover:bg-surfaces-bg-tertiary transition-colors"
                            >
                              <X className="h-3 w-3 text-text-tertiary hover:text-text-primary" />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
                <MdKeyboardArrowDown className="h-5 w-5 text-text-tertiary" />
              </div>
            </PopoverPrimitive.Trigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="start"
                className="z-50 w-[--radix-popover-trigger-width] bg-surfaces-bg-primary border border-border-tertiary rounded-2xl p-2 shadow-lg"
                style={{ width: "var(--radix-popover-trigger-width)" }}
              >
                {showSearch && (
                  <div className="sticky top-0 p-2 bg-surfaces-bg-primary mb-2 z-10">
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      onClear={() => setSearchQuery("")}
                      placeholder="Search..."
                    />
                  </div>
                )}
                <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-xl cursor-pointer hover:bg-surfaces-bg-secondary",
                          selectedValues.includes(option.value) && "bg-surfaces-bg-secondary",
                        )}
                      >
                        <CustomCheckbox
                          checked={selectedValues.includes(option.value)}
                          variant="radio"
                          className="pointer-events-none"
                        />
                        <div className="flex items-center gap-2">
                          {option.icon && <span className="flex-shrink-0 text-text-primary">{option.icon}</span>}
                          <span className="text-text-primary max-sm:text-small break-words whitespace-normal">
                            {option.label}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center py-8 text-text-tertiary">No results found</div>
                  )}
                </div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        ) : (
          <Select
            value={typeof value === "string" ? value : ""}
            onValueChange={disabled ? undefined : onValueChange}
            onOpenChange={
              disabled
                ? undefined
                : (open: boolean) => {
                    setIsFocused(open);
                    handleDropdownOpen(open);
                  }
            }
            disabled={disabled}
          >
            {hideTrigger ? (
              <div className="flex items-center justify-between">
                <CustomTrigger>{children}</CustomTrigger>
                {isLoading && <Spinner size={10} />}
              </div>
            ) : (
              <SelectTrigger
                className={cn(
                  "h-full px-4 border-0 bg-transparent focus:ring-0 focus:ring-offset-0",
                  value ? "text-text-primary" : "text-text-tertiary",
                  className,
                )}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2 min-w-0">
                    {icon && <span className="flex-shrink-0 text-text-primary">{icon}</span>}
                    {value && options.find(opt => opt.value === value) ? (
                      <>
                        {options.find(opt => opt.value === value)?.icon && (
                          <span className="flex-shrink-0 text-text-primary">
                            {options.find(opt => opt.value === value)?.icon}
                          </span>
                        )}
                        <span className="text-text-primary max-sm:text-small flex gap-2 truncate overflow-hidden whitespace-nowrap max-w-full">
                          {options.find(opt => opt.value === value)?.label}
                          {options.find(opt => opt.value === value)?.description &&
                            (typeof options.find(opt => opt.value === value)?.description === "string" ? (
                              <span className="text-text-tertiary text-small max-sm:text-small">
                                {options.find(opt => opt.value === value)?.description}
                              </span>
                            ) : (
                              options.find(opt => opt.value === value)?.description
                            ))}
                        </span>
                      </>
                    ) : (
                      <span className="text-text-tertiary truncate overflow-hidden whitespace-nowrap max-w-full">
                        {placeholder}
                      </span>
                    )}
                  </div>
                </div>
              </SelectTrigger>
            )}

            <SelectContent className="bg-surfaces-bg-primary border-border-tertiary rounded-2xl">
              {showSearch && (
                <div className="sticky top-0 p-2 bg-surfaces-bg-primary mb-2 z-10">
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onClear={() => setSearchQuery("")}
                    placeholder="Search..."
                  />
                </div>
              )}
              <div className="space-y-4 p-2">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className={cn("rounded-xl p-4 focus:bg-surfaces-bg-tertiary", "bg-surfaces-bg-secondary")}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {option.icon && <span className="flex-shrink-0 text-text-primary">{option.icon}</span>}
                          <span className="text-text-primary max-sm:text-small break-words whitespace-normal">
                            {option.label}
                          </span>
                        </div>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <div className="flex items-center justify-center py-8 text-text-tertiary">No results found</div>
                )}
              </div>
            </SelectContent>
          </Select>
        )}
      </div>

      {variant === "modal" && !disabled && (
        <Dialog
          modal={modal}
          open={isModalOpen}
          onOpenChange={(open: boolean) => {
            if (preventClose && !open) return;
            handleModalOpen(open);
          }}
        >
          <DialogContent
            size="lg"
            background="primary"
            padding="md"
            position="center-mobile-bottom"
            hideCloseButton={true}
            height="85dvh"
            layout="flex-col-overflow"
            className="gap-0"
          >
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between py-4">
                <DialogTitle className="text-h6 font-extrabold text-text-primary">
                  {modalTitle || label}
                  {required && <span className="text-small-2 text-text-error ml-1">*</span>}
                </DialogTitle>
                {!preventClose && <DialogCloseButton onClose={() => handleModalOpen(false)} />}
              </div>
            </DialogHeader>

            {modalDescription && <div className="text-text-tertiary text-small mb-4">{modalDescription}</div>}

            {showSearch && (
              <div className="shrink-0 py-4 bg-surfaces-bg-primary">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClear={() => setSearchQuery("")}
                  placeholder="Search..."
                />
              </div>
            )}

            <div className="grow overflow-y-auto custom-scrollbar">
              {layout === "list" ? (
                <div className="py-4 space-y-2">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          if (!option.isLocked) {
                            if (isMulti) {
                              handleSelect(option.value);
                            } else {
                              onValueChange?.(option.value);
                              handleModalOpen(false);
                            }
                          }
                        }}
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-xl cursor-pointer hover:bg-surfaces-bg-secondary",
                          option.isLocked && "opacity-50 cursor-not-allowed",
                          (isMulti ? selectedValues.includes(option.value) : tempSelection === option.value) &&
                            "bg-surfaces-bg-secondary",
                        )}
                      >
                        <CustomCheckbox
                          checked={isMulti ? selectedValues.includes(option.value) : tempSelection === option.value}
                          variant={isMulti ? "default" : "radio"}
                          className="pointer-events-none"
                        />
                        <div className="flex items-center gap-2">
                          {option.icon && <span className="flex-shrink-0 text-text-primary">{option.icon}</span>}
                          <span className="text-text-primary max-sm:text-small break-words whitespace-normal">
                            {option.label}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center py-8 text-text-tertiary">No results found</div>
                  )}
                </div>
              ) : (
                <div className="py-4 space-y-4">
                  {filteredOptions.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-text-tertiary">No results found</div>
                  ) : (
                    <>
                      {isSpecialSelect && tempSelection && (
                        <div className="w-full">
                          {filteredOptions
                            .filter(opt => opt.value === tempSelection)
                            .map(option => (
                              <div
                                key={option.value}
                                onClick={() => !option.isLocked && handleSelect(option.value)}
                                className="p-4 bg-surfaces-bg-secondary rounded-xl"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {option.icon && (
                                      <span className="flex-shrink-0 text-text-primary">{option.icon}</span>
                                    )}
                                    <div className="flex flex-col">
                                      <span className="text-text-primary max-sm:text-small break-words whitespace-normal">
                                        {option.label}
                                      </span>
                                      {option.description &&
                                        (typeof option.description === "string" ? (
                                          <span className="text-text-tertiary text-small max-sm:text-small break-words whitespace-normal">
                                            {option.description}
                                          </span>
                                        ) : (
                                          option.description
                                        ))}
                                    </div>
                                  </div>
                                  <CustomCheckbox checked={true} variant="radio" className="pointer-events-none" />
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                      <div className={cn("grid gap-2 grid-cols-2")}>
                        {filteredOptions
                          .filter(opt => !isSpecialSelect || opt.value !== tempSelection)
                          .map(option => {
                            const isSelected = isMulti
                              ? selectedValues.includes(option.value)
                              : tempSelection === option.value;

                            return (
                              <div
                                key={option.value}
                                onClick={() => !option.isLocked && handleSelect(option.value)}
                                className={cn(
                                  "p-4 mx-0.5 rounded-xl cursor-pointer bg-surfaces-bg-secondary",
                                  option.isLocked && "opacity-50 cursor-not-allowed",
                                  isSelected && "ring-2 ring-border-focus",
                                )}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {option.icon && (
                                      <span className="flex-shrink-0 text-text-primary">{option.icon}</span>
                                    )}
                                    <div className="flex flex-col">
                                      <span className="text-text-primary max-sm:text-small break-words whitespace-normal">
                                        {option.label}
                                      </span>
                                      {option.description &&
                                        (typeof option.description === "string" ? (
                                          <span className="text-text-tertiary text-small max-sm:text-small break-words whitespace-normal">
                                            {option.description}
                                          </span>
                                        ) : (
                                          option.description
                                        ))}
                                    </div>
                                  </div>
                                  <CustomCheckbox
                                    checked={isSelected}
                                    variant="radio"
                                    className="pointer-events-none"
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {isMulti && (
              <div className="shrink-0 py-4 mt-auto">
                <button
                  onClick={handleModalSelect}
                  disabled={selectedValues.length === 0}
                  className={cn(
                    "w-full py-3 rounded-full text-small-2",
                    selectedValues.length > 0
                      ? "bg-button-tertiary-bg text-text-primaryinverse"
                      : "bg-surfaces-bg-secondary text-text-tertiary cursor-not-allowed",
                  )}
                >
                  Select
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {hint && <p className="text-small text-text-tertiary ml-4">{hint}</p>}
    </div>
  );
}
